import { useState, useEffect, useCallback } from 'react';
import { getLandingCarouselAPI } from '../../../../../api';
import { LandingCarousel } from '../../../../../store';
import { isDebugging } from '../../../../../utility';

interface UseGetLandingCarouselReturn {
  landingCarousels: LandingCarousel[];
  loading: boolean;
  error: string | null;
  refetch: (getHidden?: boolean) => Promise<void>;
}

/**
 * Custom React hook to fetch landing carousel data directly from the API
 * @param getHidden - Whether to include hidden carousels (default: false)
 * @returns Object containing carousels data, loading state, error state, and refetch function
 */
export const useGetLandingCarousel = (getHidden = false): UseGetLandingCarouselReturn => {
  const [landingCarousels, setLandingCarousels] = useState<LandingCarousel[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCarousels = useCallback(async (includeHidden = getHidden) => {
    setLoading(true);
    setError(null);

    try {
      const res = await getLandingCarouselAPI();
      
      if (isDebugging) {
        console.log('useGetLandingCarousel - API response:', res);
      }

      const carousels: LandingCarousel[] = [];

      res.data.forEach((item) => {
        // Skip hidden items if getHidden is false
        if (!includeHidden && item.hide === 1) return;

        const modifiedItem: LandingCarousel = {
          orderNum: item.orderNum,
          id: item.id,
          image: item.image,
          link: item.link,
          headerText: item.headline,
          subHeaderText: item.subhead,
          buttonText: item.button_text,
          isHidden: item.hide === 1
        };

        carousels.push(modifiedItem);
      });

      // Sort by order number
      carousels.sort((a, b) => a.orderNum - b.orderNum);

      setLandingCarousels(carousels);

      // Cache the results in localStorage (similar to store behavior)
      if (carousels.length > 0) {
        localStorage.setItem('landingCarousel', JSON.stringify(carousels));
      }

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to retrieve landing carousel';
      setError(errorMessage);
      
      if (isDebugging) {
        console.log('useGetLandingCarousel error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [getHidden]);

  // Refetch function that can be called manually
  const refetch = useCallback(async (includeHidden?: boolean) => {
    await fetchCarousels(includeHidden);
  }, [fetchCarousels]);

  // Initial fetch on mount
  useEffect(() => {
    fetchCarousels();
  }, [fetchCarousels]);

  return {
    landingCarousels,
    loading,
    error,
    refetch
  };
};

export default useGetLandingCarousel;
